/**
 * Returns the nearest ceil 100th value to the given number
 *
 * @param value : number
 *
 * @example
 * // return 510200
 * getNearestCeilValue(510112)
 *
 *
 * @returns {number}
 */
export const getNearestCeilValue = (value: number): number =>
    value + (100 - (value % 100));

/**
 * Shorten number to thousands, millions, billions, etc.
 *
 * @param {number} num Number to shorten.
 * @param {number} [digits=0] The number of digits to appear after the decimal point.
 * @returns {number}
 *
 * @example
 * // returns '12.5k'
 * nFormatter(12543, 1)
 *
 * @example
 * // returns '-13k'
 * nFormatter(-12567)
 *
 * @example
 * // returns '51M'
 * nFormatter(51000000)
 *
 * @example
 * // returns 651
 * nFormatter(651)
 *
 * @example
 * // returns 0.12345
 * nFormatter(0.12345)
 */
export const nFormatter = (num: number, digits: number = 0): string => {
    const units = ['k', 'M', 'G', 'T', 'P', 'E', 'Z', 'Y'];
    let decimal;

    for (var i = units.length - 1; i >= 0; i--) {
        decimal = Math.pow(1000, i + 1);

        if (num <= -decimal || num >= decimal) {
            return +(num / decimal).toFixed(digits) + units[i];
        }
    }

    return num.toString();
};

export const convertNumberToMillionString = (value: number) => {
    if (value === 0) {
        return 0;
    }
    const convertValue = (value / 1000000).toFixed(2);
    return `${convertValue} Million`;
};

export const formatPercentage = (value: string) => {
    let formattedValue: number | string = parseFloat(value);

    if (formattedValue < 100) {
        formattedValue = formattedValue.toFixed(2);
    } else {
        formattedValue = formattedValue.toString();
    }

    return `${formattedValue}%`;
};

/**
 * Format a number using locale-aware thousands separators.
 * Defaults to "en-US" (e.g. 1234567 -> "1,234,567").
 *
 * Pass a locale for different grouping rules, and options for decimals/currency/etc.
 *
 * Examples:
 * formatNumberFull(1234567); // "1,234,567"
 * formatNumberFull(1234567, 'de-DE'); // "1.234.567"
 * formatNumberFull(12345.678, 'en-US', { maximumFractionDigits: 2 }); // "12,345.68"
 */
export const formatNumberFull = (
    value: number | bigint,
    locale: string = 'en-US',
    options?: Intl.NumberFormatOptions,
): string => {
    return new Intl.NumberFormat(locale, options).format(value);
};
